import { FC, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import type { TourDocumentView } from 'entities/tour/models/document'

import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import emptyImage from 'shared/images/empty-image.webp'
import { dateFormat } from 'shared/lib/dateFormat'
import { getCleanHTML } from 'shared/lib/getCleanHTML'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { Button } from 'shared/ui/button'
import { ImageOptimized } from 'shared/ui/image-optimized'
import { StarRating } from 'shared/ui/star-rating'

import common from '../../styles.module.scss'

import s from './styles.module.scss'

interface Props {
  onGoBack: () => void
  document: TourDocumentView
}

const badgeKeys: Partial<Record<TourDocumentView['status'], string>> = {
  IN_TRIP: 'tourDetails.inTrip',
  COMPLETED: 'tourDetails.completed',
  CANCELLED: 'tourDetails.cancelled',
}

export const TourSummary: FC<Props> = ({ document, onGoBack }) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [shouldShowMore, setShouldShowMore] = useState(false)
  const descriptionRef = useRef<HTMLDivElement>(null)
  const badge = badgeKeys[document.status] ?? 'tourDetails.upcoming'
  const hotelStars = document.stars
    ? Math.min(Math.max(document.stars, 1), 5)
    : 0
  const hasDates = document.startDate || document.endDate
  const hasTravelInfo =
    document.nights !== null || document.passengers.length > 0
  const hasSummary = Boolean(hasDates || hasTravelInfo || document.description)

  useEffect(() => {
    const element = descriptionRef.current

    if (!element) {
      setShouldShowMore(false)

      return
    }

    const checkLines = () => {
      const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight)
      const maxHeight = lineHeight * 3

      setShouldShowMore(element.scrollHeight > maxHeight + 1)
    }

    const frame = window.requestAnimationFrame(checkLines)

    return () => window.cancelAnimationFrame(frame)
  }, [document.description])

  return (
    <section>
      <div className={s.hero}>
        <ImageOptimized
          wrapperClassName={s.photo}
          src={document.cover ?? undefined}
          errorImage={emptyImage}
          alt=""
          priority
          objectFit="cover"
        />
        <Button
          variant="white"
          className={s.backButton}
          aria-label={t('back')}
          onClick={onGoBack}
        >
          <AppIcon
            id={AllIcons.ARROW_LEFT}
            width={24}
            height={24}
          />
        </Button>
        <div className={s.heroContent}>
          <span className={clsx(s.badge, 'semi-bold-12')}>{t(badge)}</span>
          {document.title && <h1 className="black-24">{document.title}</h1>}
          {(document.hotel || hotelStars > 0) && (
            <div className={clsx(s.hotel, 'medium-14')}>
              {document.hotel && <span>{document.hotel}</span>}
              {document.hotel && hotelStars > 0 && (
                <span
                  className={s.separator}
                  aria-hidden="true"
                />
              )}
              {hotelStars > 0 && (
                <span
                  role="img"
                  aria-label={t('tourDetails.hotelStars', {
                    count: hotelStars,
                  })}
                >
                  <StarRating
                    rating={hotelStars}
                    maxStars={hotelStars}
                    size={14}
                    className={s.rating}
                    filledIcon={AllIcons.STAR_FILLED_GOLD}
                  />
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {hasSummary && (
        <div className={clsx(common.card, s.description)}>
          <div className={common.row}>
            <div>
              {hasDates && (
                <h2 className="bold-16">
                  {document.startDate &&
                    dateFormat(document.startDate, DATE_FORMAT_LIST.DATE_MONTH)}
                  {document.startDate && document.endDate && ' – '}
                  {document.endDate &&
                    dateFormat(document.endDate, DATE_FORMAT_LIST.DATE_MONTH)}
                </h2>
              )}
              {hasTravelInfo && (
                <p className={clsx(common.caption, 'medium-14')}>
                  {document.nights !== null &&
                    t('tours_block.night', { count: document.nights })}
                  {document.nights !== null &&
                    document.passengers.length > 0 &&
                    ' · '}
                  {document.passengers.length > 0 &&
                    t('tours_block.tourists_count', {
                      count: document.passengers.length,
                    })}
                </p>
              )}
            </div>
            {hasDates && (
              <AppIcon
                id={AllIcons.CALENDAR_ALT}
                width={24}
                height={24}
              />
            )}
          </div>
          {document.description && (
            <div className={s.info}>
              <div
                ref={descriptionRef}
                id="tour-description"
                className={clsx('medium-14', s.text, {
                  [s.collapsed]: !isExpanded,
                })}
                dangerouslySetInnerHTML={{
                  __html: getCleanHTML(document.description, {
                    forbiddenTags: ['img', 'svg'],
                  }),
                }}
              />
              {shouldShowMore && (
                <Button
                  variant="transparent"
                  className={clsx(common.link, s.expand, 'medium-14')}
                  aria-expanded={isExpanded}
                  aria-controls="tour-description"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <AppIcon
                    id={AllIcons.CHEVRON_DOWN}
                    width={12}
                    height={12}
                    className={isExpanded ? s.rotated : undefined}
                  />
                  {t(isExpanded ? 'tourDetails.less' : 'tourDetails.more')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
